import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ShoppingBagIcon
} from '@heroicons/react/24/outline';
import api from '../api/axios';
import { useCart } from '../context/CartContext';
import { getPaymentStatus, getPaymentStatusMeta } from '../utils/paymentStatus';

const formatCurrency = (amount) => new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  maximumFractionDigits: 0
}).format(Number(amount || 0));

const PaymentResultPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const orderId = searchParams.get('orderId');
  // Wompi agrega `id` al volver cuando llegó a crear una transacción.
  const wompiTransactionId = searchParams.get('id') || searchParams.get('transactionId');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const verifyPayment = async () => {
      if (!orderId) {
        if (isMounted) {
          setError('No encontramos la referencia de tu compra. Puedes revisar tu carrito e intentar nuevamente.');
          setLoading(false);
        }
        return;
      }

      try {
        const { data } = await api.get(`/orders/${orderId}/verify-payment`, {
          params: wompiTransactionId ? { transactionId: wompiTransactionId } : {}
        });
        if (!isMounted) return;

        // Si el cliente volvió sin que Wompi creara una transacción, eliminamos el
        // borrador y devolvemos el inventario. El carrito permanece intacto.
        if (!wompiTransactionId && data.transactionsFound === 0 && data.order?.paymentStatus === 'pending') {
          await api.post(`/orders/${orderId}/cancel-unpaid`);
          if (!isMounted) return;
          localStorage.removeItem('pcvasquezPendingCheckout');
          setOrder({ ...data.order, paymentStatus: 'cancelled', status: 'cancelled' });
          return;
        }

        setOrder(data.order);
        if (['completed', 'failed', 'rejected', 'cancelled'].includes(data.order?.paymentStatus)) {
          localStorage.removeItem('pcvasquezPendingCheckout');
        }
        // Solo vaciamos el carrito cuando Wompi confirmó el cobro en el servidor.
        if (data.order?.paymentStatus === 'completed') {
          clearCart();
          localStorage.removeItem('pcvasquezCheckoutShippingDraft');
        }
      } catch (requestError) {
        if (isMounted) {
          setError(requestError.response?.data?.error || 'No fue posible verificar el estado del pago.');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    verifyPayment();
    return () => { isMounted = false; };
  }, [orderId, wompiTransactionId, clearCart]);

  if (loading) {
    return (
      <main className="min-h-[70vh] grid place-items-center px-4">
        <div className="text-center">
          <ArrowPathIcon className="h-12 w-12 animate-spin text-[#003a70] mx-auto" />
          <p className="mt-4 text-lg font-medium text-gray-800">Verificando el resultado de tu pago…</p>
          <p className="mt-1 text-sm text-gray-500">Esto puede tomar unos segundos.</p>
        </div>
      </main>
    );
  }

  if (error || !order) {
    return (
      <main className="min-h-[70vh] grid place-items-center bg-slate-50 px-4 py-10">
        <section className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-xl shadow-slate-200/60">
          <ExclamationTriangleIcon className="mx-auto h-14 w-14 text-amber-500" />
          <h1 className="mt-4 text-2xl font-bold text-slate-900">No pudimos confirmar el pago</h1>
          <p className="mt-3 text-slate-600">{error || 'Revisa el estado de tu orden desde tu perfil.'}</p>
          <button onClick={() => navigate('/cart')} className="mt-7 inline-flex items-center justify-center rounded-xl bg-[#003a70] px-5 py-3 font-semibold text-white transition hover:bg-[#002b54]">
            Volver al carrito
          </button>
        </section>
      </main>
    );
  }

  const normalizedPaymentStatus = getPaymentStatus(order.paymentStatus);
  const paymentMeta = getPaymentStatusMeta(order.paymentStatus);
  const isSuccess = normalizedPaymentStatus === 'completed';
  const isRejected = normalizedPaymentStatus === 'rejected';
  const isCancelled = normalizedPaymentStatus === 'cancelled';
  const appearance = isSuccess
    ? { icon: CheckCircleIcon, iconClass: 'text-emerald-500', surface: 'bg-emerald-50', title: '¡Pago confirmado!', message: 'Recibimos tu pago y tu pedido ya fue confirmado.' }
    : isRejected
      ? { icon: ExclamationTriangleIcon, iconClass: 'text-rose-500', surface: 'bg-rose-50', title: 'Pago rechazado', message: 'Wompi rechazó el pago y no se realizó ningún cobro. Puedes intentarlo nuevamente desde tu carrito.' }
      : isCancelled
        ? { icon: ExclamationTriangleIcon, iconClass: 'text-slate-500', surface: 'bg-slate-100', title: 'Pago cancelado', message: 'No se completó el pago. Tus productos permanecen en el carrito para que puedas intentarlo nuevamente.' }
      : { icon: ClockIcon, iconClass: 'text-amber-500', surface: 'bg-amber-50', title: 'Pago pendiente de confirmación', message: 'Wompi todavía no confirma el pago. No cierres esta información; puedes revisar tu orden más tarde.' };
  const StatusIcon = appearance.icon;

  return (
    <main className="min-h-[70vh] bg-slate-50 px-4 py-10 sm:py-14">
      <section className="mx-auto w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-xl shadow-slate-200/70">
        <header className={`px-6 py-8 text-center sm:px-10 ${appearance.surface}`}>
          <StatusIcon className={`mx-auto h-16 w-16 ${appearance.iconClass}`} />
          <h1 className="mt-4 text-2xl font-bold text-slate-900">{appearance.title}</h1>
          <p className="mx-auto mt-2 max-w-lg text-slate-600">{appearance.message}</p>
          <p className="mt-4 text-sm font-semibold text-slate-700">Pedido #{order.orderNumber}</p>
        </header>

        <div className="p-6 sm:p-10">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4">
            <h2 className="text-lg font-bold text-slate-900">Resumen de la compra</h2>
            <span className={`rounded-full border px-3 py-1 text-xs font-semibold tracking-wide ${paymentMeta.className}`}>{paymentMeta.label}</span>
          </div>
          <div className="divide-y divide-slate-100">
            {(order.items || []).map((item) => (
              <div key={item._id || item.product} className="flex items-center justify-between gap-4 py-4 text-sm">
                <p className="font-medium text-slate-700">{item.name} <span className="font-normal text-slate-500">× {item.quantity}</span></p>
                <p className="whitespace-nowrap font-semibold text-slate-900">{formatCurrency(item.price * item.quantity)}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-between border-t border-slate-200 pt-5 text-lg font-bold text-slate-900">
            <span>Total</span><span>{formatCurrency(order.total)}</span>
          </div>

          {order.shippingInfo && (
            <div className="mt-7 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              <p className="font-semibold text-slate-800">Entrega a nombre de {order.shippingInfo.name}</p>
              <p className="mt-1">{order.shippingInfo.address}, {order.shippingInfo.city}{order.shippingInfo.state ? `, ${order.shippingInfo.state}` : ''}</p>
            </div>
          )}

          <button
            onClick={() => navigate(isSuccess ? '/profile' : '/cart')}
            className="mt-8 flex w-full items-center justify-center gap-2 rounded-xl bg-[#003a70] px-5 py-3 font-semibold text-white transition hover:bg-[#002b54]"
          >
            <ShoppingBagIcon className="h-5 w-5" />
            {isSuccess ? 'Ver mis pedidos' : 'Volver al carrito'}
          </button>
        </div>
      </section>
    </main>
  );
};

export default PaymentResultPage;
